/**
 * Catalogue de services de la prospection — étape 1.
 *
 * Usage restant :
 *  1. **Aide à l'appel** : les `services[]` extraits par `extraireServices` s'affichent en badges
 *     **en lecture seule** sur la fiche prospect (bloc « Activité »). Rien n'atterrit tout seul dans
 *     un formulaire client — le prospect coche à l'étape 2.
 *
 * `SERVICES_PAR_METIER` (le catalogue à cocher) n'est plus utilisé par l'étape 1 depuis le
 * 30/10/2026 : la saisie des services appartient à l'étape 2. L'export est conservé pour elle,
 * ainsi que `descriptionService()`, utilisé par `extraireServices`.
 *
 * Les mots-clés et leur ordre viennent du comptage réel des 33 `ownerDescription` du run du
 * 29/10/2026 : `plomberie` 24, `chauffage` 17, `dépannage` 15 (14 + 1 sans accent), etc.
 * Le comptage montre les **deux orthographes** → la normalisation sans accents est obligatoire,
 * sinon on perd une fiche sur deux.
 *
 * Aucune dépendance, aucun I/O.
 */

/** Minuscules + accents retirés. `"Dépannage"` → `"depannage"`. */
export const normaliserTexte = (texte) =>
  String(texte ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Dictionnaire calibré sur les fréquences réelles, **du plus fréquent au moins fréquent**
 * (l'ordre pilote l'ordre d'affichage des badges).
 */
export const MOTS_CLES_SERVICES = [
  { motsCles: ['plomberie'], nom: 'Plomberie' },
  { motsCles: ['chauffage'], nom: 'Chauffage' },
  { motsCles: ['depannage', 'dépannage'], nom: 'Dépannage' },
  { motsCles: ['installation'], nom: 'Installation' },
  { motsCles: ['renovation', 'rénovation'], nom: 'Rénovation' },
  { motsCles: ['entretien'], nom: 'Entretien' },
  { motsCles: ['chaudiere', 'chaudière'], nom: 'Entretien de chaudière' },
  { motsCles: ['sanitaire'], nom: 'Sanitaire' },
  { motsCles: ['salle de bain'], nom: 'Salle de bain' },
  { motsCles: ['electricite', 'électricité'], nom: 'Électricité' },
  { motsCles: ['devis'], nom: 'Devis gratuit' },
  { motsCles: ['fuite'], nom: 'Recherche de fuite' },
  { motsCles: ['canalisation'], nom: 'Canalisation' },
  { motsCles: ['carrelage'], nom: 'Carrelage' },
  { motsCles: ['pompe a chaleur', 'pompe à chaleur', 'pac'], nom: 'Pompe à chaleur' },
  { motsCles: ['maconnerie', 'maçonnerie'], nom: 'Maçonnerie' },
  { motsCles: ['couverture'], nom: 'Couverture' },
  { motsCles: ['isolation'], nom: 'Isolation' },
  { motsCles: ['debouchage', 'débouchage'], nom: 'Débouchage' },
  { motsCles: ['menuiserie'], nom: 'Menuiserie' },
  { motsCles: ['climatisation'], nom: 'Climatisation' },
  { motsCles: ['chauffe-eau', 'chauffe eau'], nom: 'Chauffe-eau' }
];

/** Catégories Google Maps réellement rencontrées → métier interne. */
export const CATEGORIES_VERS_METIER = {
  Plombier: 'plomberie',
  Chauffagiste: 'chauffage',
  Électricien: 'electricite',
  Electricien: 'electricite',
  Maçon: 'maconnerie',
  Macon: 'maconnerie',
  Couvreur: 'couverture',
  Carreleur: 'carrelage',
  Serrurier: 'serrurerie',
  Ramoneur: 'ramonage',
  Menuisier: 'menuiserie',
  'Entreprise d’isolation': 'isolation',
  'Installateur de climatisation': 'climatisation',
  Peintre: 'peinture'
};

/**
 * Catégories **génériques ignorées** : elles n'identifient aucun métier et polluent le badge
 * « métier » comme l'extraction. Mesurées sur le dataset (`Service establishment` partout).
 */
export const CATEGORIES_GENERIQUES = [
  'Service establishment',
  'Point de service',
  'Entrepreneur',
  'Entreprise de construction',
  'Travaux généraux',
  "Fournisseur d'équipements de chauffage",
  'Fournisseur de matériel de chauffage',
  'Entreprise de plomberie',
  'Contractor',
  'Association or organization'
];

/** Faux si la catégorie n'identifie aucun métier. */
export const estCategorieGenerique = (categorie) =>
  CATEGORIES_GENERIQUES.some(
    (generique) => normaliserTexte(generique) === normaliserTexte(categorie)
  );

/** Métier interne déduit des catégories Google (C8 : `categoryName` n'est qu'indicatif). */
export const metierDepuisCategories = (categories = []) => {
  if (!Array.isArray(categories)) return null;

  for (const categorie of categories) {
    const brut = String(categorie ?? '').trim();
    if (!brut) continue;
    const direct = CATEGORIES_VERS_METIER[brut];
    if (direct) return direct;

    // Correspondance insensible à la casse / aux accents (exports retravaillés à la main)
    const normalisee = normaliserTexte(brut);
    const trouve = Object.keys(CATEGORIES_VERS_METIER).find(
      (cle) => normaliserTexte(cle) === normalisee
    );
    if (trouve) return CATEGORIES_VERS_METIER[trouve];
  }

  return null;
};

/** Métiers cibles de la prospection (scoring : 10 points, cf. C8). */
export const METIERS_CIBLES = ['plomberie', 'chauffage'];

/**
 * Libellés lisibles des métiers internes — clé = code stocké dans `targetTrade`.
 * L'ordre pilote `METIERS_CONNUS` (donc la reconnaissance d'une saisie libre).
 */
export const LIBELLES_METIERS = {
  plomberie: 'Plomberie',
  chauffage: 'Chauffage',
  electricite: 'Électricité',
  maconnerie: 'Maçonnerie',
  couverture: 'Couverture',
  carrelage: 'Carrelage',
  serrurerie: 'Serrurerie',
  ramonage: 'Ramonage',
  menuiserie: 'Menuiserie',
  isolation: 'Isolation',
  climatisation: 'Climatisation',
  peinture: 'Peinture'
};

/**
 * Métiers internes connus, sous leur forme **nominale** (`plomberie`, pas `plombier`).
 * Sert à reconnaître une saisie libre : `CATEGORIES_VERS_METIER` ne contient que les noms de
 * catégorie Google, jamais le code lui-même.
 */
export const METIERS_CONNUS = Object.keys(LIBELLES_METIERS);

/**
 * Code interne → libellé lisible. Une **saisie libre** inconnue est renvoyée telle quelle :
 * l'affichage ne doit jamais masquer ce que l'opérateur a écrit.
 *
 * @param {string|null} metier
 * @returns {string|null}
 */
export const libelleMetier = (metier) => {
  const brut = String(metier ?? '').trim();
  if (!brut) return null;
  return LIBELLES_METIERS[brut] || brut;
};

/**
 * Saisie libre du formulaire manuel → code interne, ou `null` si le métier n'est pas reconnu.
 *
 * Tolérant par construction : casse et accents sont ignorés (« Électricité », « electricite » et
 * « Électricien » donnent tous `electricite`). Une saisie **multi-métier** (« plomberie et
 * chauffage ») ou hors nomenclature renvoie `null` : elle est conservée telle quelle par l'appelant,
 * mais ne peut pas rapporter les 10 points de métier, qui sont indexés sur un code unique.
 *
 * @param {string|null} texte
 * @returns {string|null} code interne, ou `null`
 */
export const metierDepuisTexteLibre = (texte) => {
  const normalise = normaliserTexte(texte).trim();
  if (!normalise) return null;

  const code = METIERS_CONNUS.find((metier) => metier === normalise);
  if (code) return code;

  // « Plombier », « chauffagiste », « électricien »… : les alias des catégories Google.
  return metierDepuisCategories([texte]);
};

/** Gabarit de description personnalisé avec la ville (sans ville : phrase générique). */
export const descriptionService = (nom, ville) => {
  const suffixe = ville ? ` à ${ville} et ses environs` : null;
  return suffixe ? `${nom}${suffixe}.` : `${nom}.`;
};

/**
 * Catalogue à cocher sur la fiche, par métier.
 * Les descriptions sont **rédigées une fois ici** (jamais générées à la volée en base) ; `ville`
 * est passée en argument de `descriptionService()` au moment de l'affichage.
 */
export const SERVICES_PAR_METIER = {
  plomberie: [
    { nom: 'Dépannage plomberie', prix: 'Sur devis', motsCles: ['depannage', 'plomberie', 'fuite'] },
    { nom: 'Installation sanitaire', prix: 'Sur devis', motsCles: ['installation', 'sanitaire'] },
    { nom: 'Rénovation salle de bain', prix: 'Sur devis', motsCles: ['renovation', 'salle de bain'] },
    { nom: 'Recherche de fuite', prix: 'Sur devis', motsCles: ['fuite', 'canalisation'] },
    { nom: 'Débouchage canalisation', prix: 'Sur devis', motsCles: ['debouchage', 'canalisation'] }
  ],
  chauffage: [
    { nom: 'Installation chauffage', prix: 'Sur devis', motsCles: ['chauffage', 'installation'] },
    { nom: 'Entretien chaudière', prix: 'Sur devis', motsCles: ['chaudiere', 'entretien'] },
    { nom: 'Pompe à chaleur', prix: 'Sur devis', motsCles: ['pompe a chaleur', 'pac'] },
    { nom: 'Dépannage chauffage', prix: 'Sur devis', motsCles: ['depannage', 'chauffage'] }
  ],
  electricite: [
    { nom: 'Dépannage électrique', prix: 'Sur devis', motsCles: ['depannage', 'electricite'] },
    { nom: 'Mise aux normes', prix: 'Sur devis', motsCles: ['normes', 'installation'] },
    { nom: 'Installation électrique', prix: 'Sur devis', motsCles: ['installation', 'electricite'] }
  ],
  maconnerie: [
    { nom: 'Travaux de maçonnerie', prix: 'Sur devis', motsCles: ['maconnerie', 'travaux'] },
    { nom: 'Rénovation de façade', prix: 'Sur devis', motsCles: ['renovation', 'facade'] }
  ],
  couverture: [
    { nom: 'Réparation de toiture', prix: 'Sur devis', motsCles: ['couverture', 'reparation'] },
    { nom: 'Rénovation de toiture', prix: 'Sur devis', motsCles: ['couverture', 'renovation'] }
  ],
  carrelage: [
    { nom: 'Pose de carrelage', prix: 'Sur devis', motsCles: ['carrelage', 'pose'] }
  ],
  serrurerie: [
    { nom: 'Dépannage serrurerie', prix: 'Sur devis', motsCles: ['serrurerie', 'depannage'] }
  ],
  ramonage: [{ nom: 'Ramonage', prix: 'Sur devis', motsCles: ['ramonage'] }],
  menuiserie: [
    { nom: 'Menuiserie sur mesure', prix: 'Sur devis', motsCles: ['menuiserie'] }
  ],
  isolation: [{ nom: 'Isolation', prix: 'Sur devis', motsCles: ['isolation'] }],
  climatisation: [
    { nom: 'Installation climatisation', prix: 'Sur devis', motsCles: ['climatisation'] }
  ],
  peinture: [{ nom: 'Travaux de peinture', prix: 'Sur devis', motsCles: ['peinture'] }]
};
